import { Component, OnChanges, AfterViewInit, ElementRef, SimpleChanges, input, viewChild, inject } from '@angular/core';
import { AvatarService, Palette, PX, SpriteData } from '../../services/avatar.service';
import { hexToRgb } from '../../types/animation';

@Component({
  selector: 'app-pixel-creature',
  template: `<canvas #canvas [style.image-rendering]="'pixelated'"></canvas>`,
  styles: [`
    :host { display: inline-block; line-height: 0; }
    canvas { display: block; }
  `],
})
export class PixelCreatureComponent implements AfterViewInit, OnChanges {
  private readonly avatarService = inject(AvatarService);

  readonly addr = input('');
  /** Optional display size in CSS pixels. Internal resolution stays at the
   *  native sprite grid so `image-rendering: pixelated` upscales crisply. */
  readonly size = input<number>();

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

  ngAfterViewInit(): void {
    this.draw();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['addr'] || changes['size']) && this.canvasRef()) {
      this.draw();
    }
  }

  private draw(): void {
    const sprite = this.avatarService.getSprite(this.addr());
    this.paintCanvas(sprite);
  }

  private paintCanvas(sprite: SpriteData): void {
    const { pixels, pal, G } = sprite;

    const canvas = this.canvasRef().nativeElement;
    canvas.width  = G;
    canvas.height = G;
    const display = this.size() ?? G;
    canvas.style.width  = display + 'px';
    canvas.style.height = display + 'px';

    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.createImageData(G, G);
    const data = imageData.data;
    const colorMap = buildColorMap(pal);

    for (let i = 0; i < pixels.length; i++) {
      const [r, g, b] = colorMap[pixels[i]];
      const o = i * 4;
      data[o]     = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = pixels[i] === PX.BG ? 0 : 255;
    }

    ctx.putImageData(imageData, 0, 0);
  }
}

function buildColorMap(pal: Palette): [number, number, number][] {
  return [
    hexToRgb(pal.bg),       // PX.BG        = 0
    hexToRgb(pal.outline),  // PX.OUTLINE   = 1
    hexToRgb(pal.body),     // PX.BODY      = 2
    hexToRgb(pal.hi),       // PX.HI        = 3
    hexToRgb(pal.eye),      // PX.EYE       = 4
    hexToRgb(pal.mark),     // PX.MARK      = 5
    [255, 255, 255],        // PX.EYE_SHINE = 6
  ];
}
